import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Users, Mail, MessageCircle, ChevronRight, Zap, Target, Trophy, ArrowRight } from 'lucide-react';

const LevelUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    setIsSubmitted(true);
    setEmail('');
    // Reset after 3 seconds
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Real-Time Analytics",
      description: "Get instant feedback on your performance with AI-powered insights"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Personalized Training",
      description: "Customized training plans based on your unique playing style"
    },
    {
      icon: <Trophy className="w-8 h-8" />,
      title: "Performance Tracking",
      description: "Track your progress and see improvements over time"
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cresento-primary to-cresento-dark text-white py-24 pt-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-cresento-light to-cresento-accent bg-clip-text text-transparent">
              Level Up Your Game
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Join the revolution in football analytics. Experience the future of sports technology with Cresento's AI-powered smart shin pads.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-dark/40 to-black">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-4">
              The Future of Football Analytics
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Experience professional-level performance insights with our AI-powered smart shin pads
            </p>
          </motion.div>

          {/* Product Video/Image Placeholder */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mb-16"
          >
            <div className="relative bg-gradient-to-br from-cresento-primary/20 to-cresento-dark/40 rounded-2xl overflow-hidden shadow-2xl border border-cresento-secondary/30">
              {/* Placeholder for product video/image */}
              <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-cresento-secondary/10 to-cresento-accent/10">
                <div className="text-center">
                  <Play className="w-16 h-16 text-cresento-accent mx-auto mb-4" />
                  <p className="text-xl text-white font-semibold">Product Demo Video</p>
                  <p className="text-gray-300">See Cresento Smart Shin Pads in Action</p>
                </div>
              </div>
              
              {/* Video overlay with play button */}
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300 cursor-pointer">
                <div className="w-20 h-20 bg-cresento-secondary/80 rounded-full flex items-center justify-center">
                  <Play className="w-8 h-8 text-white ml-1" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 text-center"
              >
                <div className="text-cresento-accent mb-4 flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Community Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-black to-cresento-primary/20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-6">
              Join Our Community
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              Become part of a growing community of athletes who are revolutionizing their game with data-driven insights.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-cresento-primary/20 to-cresento-dark/30 rounded-2xl p-6 border border-cresento-secondary/30"
              >
                <Users className="w-12 h-12 text-cresento-secondary mb-4 mx-auto" />
                <h3 className="text-2xl font-bold mb-3 text-white">Elite Athletes</h3>
                <p className="text-gray-300">
                  Connect with professional players and learn from their experiences using Cresento technology.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-cresento-accent/20 to-cresento-dark/30 rounded-2xl p-6 border border-cresento-accent/30"
              >
                <Target className="w-12 h-12 text-cresento-accent mb-4 mx-auto" />
                <h3 className="text-2xl font-bold mb-3 text-white">Rising Stars</h3>
                <p className="text-gray-300">
                  Join ambitious players who are using analytics to accelerate their development and reach new heights.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sign Up for Updates Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-primary/20 to-cresento-dark/30">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <Mail className="text-cresento-accent mx-auto mb-6" size={64} />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Get Exclusive Updates
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Be the first to know about product launches, new features, and exclusive early access opportunities.
            </p>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gradient-to-r from-cresento-secondary to-cresento-accent rounded-lg p-6"
              >
                <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
                <p className="text-white">We'll keep you updated on our progress.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-6 py-4 bg-cresento-primary/20 backdrop-blur border border-cresento-secondary/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-cresento-secondary"
                />
                <button 
                  type="submit" 
                  className="bg-gradient-to-r from-cresento-secondary to-cresento-accent hover:from-cresento-secondary/90 hover:to-cresento-accent/90 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center"
                >
                  Sign Up <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Talk to Us Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-dark/30 to-black">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-6">
              Ready to Transform Your Game?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              Have questions? Want to learn more? Our team is here to help you understand how Cresento can elevate your performance.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cresento-secondary to-cresento-accent hover:from-cresento-secondary/90 hover:to-cresento-accent/90 text-white px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center transition-all duration-300"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Get in Touch
              </motion.a>

              <motion.a
                href="mailto:hello@cresento.com"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border border-cresento-secondary text-cresento-secondary hover:bg-cresento-secondary/10 px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center transition-all duration-300"
              >
                <Mail className="mr-2 h-5 w-5" />
                Email Us
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LevelUp;